import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

interface SidebarContextType {
  isCollapsed: boolean;
  isHovering: boolean;
  toggleSidebar: () => void;
  setHovering: (hovering: boolean) => void;
  effectiveWidth: number;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const EXPANDED_WIDTH = 256; // 16rem = 256px (w-64)
const COLLAPSED_WIDTH = 64; // 4rem = 64px (w-16)

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const toggleSidebar = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const setHovering = useCallback((hovering: boolean) => {
    setIsHovering(hovering);
  }, []);

  // Effective width considers hover state - when hovering, sidebar expands
  const effectiveWidth = isCollapsed && !isHovering ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  return (
    <SidebarContext.Provider
      value={{
        isCollapsed,
        isHovering,
        toggleSidebar,
        setHovering,
        effectiveWidth,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

export { EXPANDED_WIDTH, COLLAPSED_WIDTH };
